import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { agencyPerformanceAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  TrendingUp, Users, Shield, CheckCircle, XCircle,
  Loader2, ArrowRight, Award
} from 'lucide-react';

export function PerformanceScoreboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await agencyPerformanceAPI.scoreboard();
      setData(res.data);
    } catch (err) {
      console.error('Failed to load scoreboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="performance-scoreboard">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.scoreboard?.length) {
    return (
      <Card data-testid="performance-scoreboard">
        <CardContent className="flex flex-col items-center justify-center py-12 text-slate-400">
          <Users size={32} className="mb-2" />
          <p className="text-sm">No agents in your downline</p>
        </CardContent>
      </Card>
    );
  }

  const { scoreboard, summary } = data;

  return (
    <Card data-testid="performance-scoreboard">
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <TrendingUp size={16} className="text-sky-500" />
          Downline Performance
        </CardTitle>
        <Badge variant="outline" className="text-xs">{summary.total_agents} agents</Badge>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Summary Bar */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-center">
            <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-400">{summary.active_agents}</p>
            <p className="text-[10px] text-emerald-600 dark:text-emerald-500">Active</p>
          </div>
          <div className="p-3 rounded-lg bg-sky-50 dark:bg-sky-900/20 text-center">
            <p className="text-lg font-semibold text-sky-700 dark:text-sky-400">{summary.compliance_rate}%</p>
            <p className="text-[10px] text-sky-600 dark:text-sky-500">Compliant</p>
          </div>
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-center">
            <p className="text-lg font-semibold text-amber-700 dark:text-amber-400">{summary.avg_appointments}</p>
            <p className="text-[10px] text-amber-600 dark:text-amber-500">Avg Appts</p>
          </div>
        </div>

        {/* Agent List */}
        <ScrollArea className="h-[300px]">
          <div className="space-y-2">
            {scoreboard.slice(0, 20).map((agent, i) => (
              <Link
                key={agent.npn}
                to={`/agents/${agent.npn}`}
                className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
                data-testid={`scoreboard-agent-${i}`}
              >
                {/* Rank */}
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                  i === 0 ? 'bg-amber-100 text-amber-700' :
                  i === 1 ? 'bg-slate-100 text-slate-700' :
                  i === 2 ? 'bg-orange-100 text-orange-700' :
                  'bg-slate-50 text-slate-500 dark:bg-slate-800'
                }`}>
                  {i < 3 ? <Award size={12} /> : i + 1}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-900 dark:text-slate-100 truncate">{agent.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-slate-400">{agent.active_appointments} appts</span>
                    <span className="text-[10px] text-slate-300">|</span>
                    <span className="text-[10px] text-slate-400">{agent.pending_requests} pending</span>
                  </div>
                </div>

                {/* Status indicators */}
                <div className="flex items-center gap-1 shrink-0">
                  {agent.compliance_complete ? (
                    <Shield size={12} className="text-emerald-500" title="Compliant" />
                  ) : (
                    <Shield size={12} className="text-red-400" title="Incomplete compliance" />
                  )}
                  <Badge variant="outline" className={`text-[9px] ${
                    agent.status === 'active' ? 'text-emerald-600 border-emerald-300' :
                    agent.status === 'onboarding' ? 'text-amber-600 border-amber-300' :
                    'text-slate-500 border-slate-300'
                  }`}>
                    {agent.status}
                  </Badge>
                </div>
                <ArrowRight size={12} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </Link>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
